import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../../components/Layout';
import FuelPriceTable from '../../components/FuelPriceTable';
import SEOContent from '../../components/SEOContent';
import useTheme from '../../hooks/useTheme';
import useExchangeRate from '../../hooks/useExchangeRate';

const LitersToGallonsEN = () => {
  const { darkMode, toggleDarkMode, cardClass, inputClass } = useTheme();
  const { dollarRate } = useExchangeRate();

  const [liters, setLiters] = useState('');
  const [result, setResult] = useState(null);

  const handleConvert = (value) => {
    setLiters(value);
    if (value && parseFloat(value) > 0) {
      const litersNum = parseFloat(value);
      const gallons = litersNum / 3.78541;
      setResult({
        liters: litersNum,
        gallons: gallons.toFixed(2),
        gallonsRounded: Math.round(gallons * 10) / 10
      });
    } else {
      setResult(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Liters to Gallons Converter - Free Calculator | Gallons&Liters</title>
        <meta name="description" content="Convert liters to US gallons instantly. Free and accurate liters to gallons calculator with US fuel price table." />
        <meta name="keywords" content="liters to gallons, liter to gallon converter, liters gallons calculator, how many gallons in a liter" />
        <link rel="canonical" href="https://galonyilitry.pl/en/liters-to-gallons-converter" />
        <html lang="en" />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="en">
        <div className="max-w-4xl mx-auto pt-20 pb-12 px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-4">
              Liters to Gallons Converter
            </h1>
            <p className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Quickly convert liters to US gallons. 1 liter = 0.264 gallons
            </p>
          </div>

          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Conversion Calculator
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Liters:
                </label>
                <input
                  type="number"
                  className={`w-full px-4 py-3.5 border rounded-xl shadow-sm focus:outline-none focus:ring-2 transition-all input-green ${inputClass}`}
                  placeholder="Enter liters"
                  value={liters}
                  onChange={(e) => handleConvert(e.target.value)}
                />
              </div>

              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Gallons (US):
                </label>
                <div className={`w-full px-4 py-3.5 border rounded-xl ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <span className={`text-xl font-bold ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                    {result ? `${result.gallons} gal` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {result && (
              <div className={`mt-6 p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-700 to-cyan-700' : 'bg-gradient-to-r from-blue-600 to-cyan-500'} text-white`}>
                <p className="text-center text-lg">
                  <span className="font-bold">{result.liters}</span> liters = <span className="font-bold">{result.gallons}</span> gallons
                </p>
                <p className="text-center text-sm mt-1 opacity-80">
                  (approximately {result.gallonsRounded} gal)
                </p>
              </div>
            )}

            <div className={`mt-6 grid grid-cols-2 md:grid-cols-4 gap-3`}>
              {[5, 10, 20, 50].map((lit) => (
                <button
                  key={lit}
                  onClick={() => handleConvert(lit.toString())}
                  className={`p-3 rounded-lg text-center transition-all ${
                    darkMode
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                >
                  <div className="font-bold">{lit} L</div>
                  <div className="text-sm opacity-80">{(lit / 3.78541).toFixed(1)} gal</div>
                </button>
              ))}
            </div>
          </div>

          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Liters to Gallons Conversion Table
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700' : 'bg-blue-50'}>
                    <th className={`px-4 py-2 text-left ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Liters</th>
                    <th className={`px-4 py-2 text-right ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Gallons</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 5, 10, 15, 20, 30, 40, 50, 75, 100].map((lit) => (
                    <tr key={lit} className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                      <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{lit} L</td>
                      <td className={`px-4 py-2 text-right font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {(lit / 3.78541).toFixed(2)} gal
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-8">
            <FuelPriceTable darkMode={darkMode} dollarRate={dollarRate} lang="en" />
          </div>

          <SEOContent darkMode={darkMode} title="How to Convert Liters to Gallons?">
            <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Formula for Converting Liters to Gallons
            </h3>
            <p>
              Converting liters to US gallons requires dividing the number of liters by <strong>3.78541</strong>.
              You can also multiply by <strong>0.264172</strong>. For quick mental calculations, use 0.26 or simply divide by 4 and add 5%.
            </p>
            <p className="mt-3">
              <strong>Formula:</strong> Gallons = Liters ÷ 3.78541 <br />
              <strong>Alternatively:</strong> Gallons = Liters × 0.264172
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Why Know the Conversion?
            </h3>
            <p>
              Knowing the liters to gallons conversion is essential when traveling in the USA. At American gas stations,
              fuel prices are displayed in dollars per gallon. Knowing the conversion, you can easily calculate
              how much fuel you need and compare prices with European ones.
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Conversion Examples
            </h3>
            <ul className={`list-disc list-inside mt-2 space-y-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <li><strong>50 liters</strong> = 13.21 gallons (typical European car tank)</li>
              <li><strong>60 liters</strong> = 15.85 gallons (SUV tank)</li>
              <li><strong>80 liters</strong> = 21.13 gallons (larger vehicle tank)</li>
            </ul>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Tips for US Travelers
            </h3>
            <p>
              When planning a road trip in the States, know that a typical American car has a fuel tank capacity
              of 12-18 gallons (45-68 liters). When renting a car, check the tank capacity in the specifications -
              this will help you plan refueling stops during your road trip.
            </p>
          </SEOContent>
        </div>
      </Layout>
    </>
  );
};

export default LitersToGallonsEN;
